import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import { Grid, Row, Col } from 'react-bootstrap';
import { isEmpty as _isEmpty, isEqual as _isEqual } from 'lodash';

import { setFilters } from 'actions/SalesActivityDashboardActions';
import { changeActivePage, changeListSort } from 'actions/ListActions';
import { addNotification } from 'actions/NotificationsActions';
import { parseQs, stringifyQs } from 'helpers/http';

import DocMeta from 'components/DocMeta';
import List from 'components/List';

import VklFilterBar from 'components/layout/vkl/VklFilterBar';
import VklBusinessList from 'components/layout/vkl/VklBusinessList';
import VklBarChart from 'components/layout/vkl/VklBarChart';
import VklSalesmanList from 'components/layout/vkl/VklSalesmanList';

import Pagination from 'components/layout/Pagination';
import Loader from 'components/layout/Loader';

class VklBusinessesView extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      currentSalesmanListSortKey: 'salesman_lastname',
      currentSalesmanListSortOrder: 'asc',
      activityListSortKey: 'referenceDate',
      activityListSortOrder: 'desc',
      initialised: true, // indicate if the component may be rendered
    };

    const query = parseQs(props.location.search);

    if (query && !_isEmpty(query) && !_isEqual(query, props.filters)) {
      props.setFilters(query);
      this.state.initialised = false; // we need to wait until the filters state get updated
    } else if (props.filters && !_isEmpty(props.filters)) {
      // if there filters set in state, use them
      props.history.replace({
        pathname: props.location.pathname,
        search: stringifyQs(props.filters),
      });
    } else {
      props.history.replace({
        pathname: props.location.pathname,
        search: 'year[]=' + new Date().getFullYear(),
      });
      this.state.initialised = false; // we need to wait until the filters state get updated
    }

    if (props.location && props.location.search) {
      props.setFilters(parseQs(props.location.search));
    }
  }

  componentDidUpdate = prevProps => {
    const { location, filters, setFilters } = this.props;
    const { initialised } = this.state;

    if (!_isEqual(prevProps.location.search, location.search)) {
      setFilters(parseQs(location.search));
    }

    if (!initialised && !_isEqual(prevProps.filters, filters)) {
      this.setState({ initialised: true });
    }
  };

  onYearSelect = year => {
    const { history, location, setFilters } = this.props;

    const query = parseQs(location.search);
    query.year = [year];
    history.push({ pathname: location.pathname, search: stringifyQs(query) });
    setFilters(query);
  };

  onMonthSelect = month => {
    const { history, location, setFilters } = this.props;

    const query = parseQs(location.search);
    query.month = [month];
    history.push({ pathname: location.pathname, search: stringifyQs(query) });
    setFilters(query);
  };

  onActivityListSort = key => {
    let sortOrder = 'desc';
    if (this.state.activityListSortKey === key) {
      sortOrder = this.state.activityListSortOrder === 'desc' ? 'asc' : 'desc';
    }

    this.setState({
      activityListSortKey: key,
      activityListSortOrder: sortOrder,
    });
    this.props.changeListSort('vklBusinesses', key, sortOrder);
  };

  onSalesmanListSort = key => {
    let sortOrder = 'asc';
    if (this.state.currentSalesmanListSortKey === key) {
      sortOrder = this.state.currentSalesmanListSortOrder === 'desc' ? 'asc' : 'desc';
    }
    this.setState({
      currentSalesmanListSortKey: key,
      currentSalesmanListSortOrder: sortOrder,
    });
  };

  onSalesmanSelect = salesmanId => {
    const { history, location, setFilters } = this.props;

    const query = parseQs(location.search);
    query.salesmanId = [salesmanId];
    history.push({ pathname: location.pathname, search: stringifyQs(query) });
    setFilters(query);
  };

  handlePagination = page => {
    // when the page changes, we need to set the current state, then dispatch new event to fetch list
    const { vklBusinesses } = this.props;
    this.props.changeActivePage('vklBusinesses', page, vklBusinesses.resPerPage);
  };

  render() {
    const { location, vklBusinesses, salesmanList, chartList, filters, vklFacets, filtersets } = this.props;
    const { initialised } = this.state;

    const urlData = { filters: { ...parseQs(location.search), ...filters } };
    const attributes = { list: [], count: 0 };

    return initialised ? (
      <DocMeta tags={[{ name: 'title', content: 'Sales Activity Dashboard' }]}>
        <div class="p-t-20">
          <List
            noRender
            listName="vklBusinesses"
            url="/api/plugin/salesActivityDashboard/activities.json"
            urlData={urlData}
            list={vklBusinesses}
          />
          <List
            noRender
            listName="filtersets"
            url="/api/plugin/salesActivityDashboard/filtersets.json"
            urlData={urlData}
            list={filtersets}
          />
          <List
            noRender
            listName="salesmanList"
            url="/api/plugin/salesActivityDashboard/salesmen.json"
            urlData={urlData}
            list={salesmanList}
          />
          <List
            noRender
            listName="chartList"
            url="/api/plugin/salesActivityDashboard/chart.json"
            urlData={urlData}
            list={chartList}
          />
          <List
            noRender
            listName="vklFacets"
            url="/api/plugin/salesActivityDashboard/facets.json"
            urlData={urlData}
            list={vklFacets}
          />

          <Grid fluid>
            {!vklFacets.pending && !filtersets.pending ? (
              <div>
                <VklFilterBar attributes={attributes} facets={vklFacets} location={location} filtersets={filtersets} />
                <hr class="m-t-10" />
              </div>
            ) : (
              <div class="p-t-80 p-b-80">
                <Loader />
              </div>
            )}

            {!salesmanList.pending && !chartList.pending ? (
              <Row>
                <Col xs={12} md={12} lg={5}>
                  <VklSalesmanList
                    selectedSalesmanList={urlData.filters.salesmanId}
                    salesmanList={salesmanList.data.list}
                    sortKey={this.state.currentSalesmanListSortKey}
                    sortOrder={this.state.currentSalesmanListSortOrder}
                    onSort={this.onSalesmanListSort}
                    onSelect={this.onSalesmanSelect}
                  />
                </Col>
                <Col xs={12} md={12} lg={7}>
                  {!chartList.pending ? (
                    <VklBarChart
                      location={location}
                      data={chartList.data.list}
                      onYearSelect={this.onYearSelect}
                      onMonthSelect={this.onMonthSelect}
                    />
                  ) : null}
                </Col>
              </Row>
            ) : (
              <div class="p-t-150 p-b-150">
                <Loader />
              </div>
            )}

            {!vklBusinesses.pending ? (
              <Row>
                <Col xs={12} class="p-b-30">
                  <VklBusinessList
                    activityList={vklBusinesses.data.list}
                    sortKey={this.state.activityListSortKey}
                    sortOrder={this.state.activityListSortOrderAsc}
                    onSort={this.onActivityListSort}
                  />

                  {vklBusinesses.data.count > 0 ? (
                    <Pagination
                      count={vklBusinesses.data.count}
                      resPerPage={vklBusinesses.resPerPage}
                      activePage={vklBusinesses.activePage}
                      handleSelect={this.handlePagination}
                    />
                  ) : (
                    <Row>
                      <Col md={12}>
                        <div>Keine Daten vorhanden</div>
                      </Col>
                    </Row>
                  )}
                </Col>
              </Row>
            ) : (
              <div class="p-t-50 p-b-50">
                <Loader />
              </div>
            )}
          </Grid>
        </div>
      </DocMeta>
    ) : null;
  }
}

const mapStateToProps = state => {
  return {
    appIntl: state.intl,
    projectConfig: state.data.projectConfig,
    vklBusinesses: state.list.vklBusinesses,
    salesmanList: state.list.salesmanList,
    vklFacets: state.list.vklFacets,
    chartList: state.list.chartList,
    user: state.login.user,
    filters: state.salesActivityDashboard,
    filtersets: state.list.filtersets,
  };
};
const mapDispatchToProps = dispatch => {
  return {
    setFilters: filters => {
      dispatch(setFilters(filters));
    },
    changeActivePage: (listName, activePage, resPerPage) => {
      dispatch(changeActivePage(listName, activePage, resPerPage));
    },
    changeListSort: (listName, sort, dir) => {
      dispatch(changeListSort(listName, sort, dir));
    },
    doNotify: (code, text) => {
      dispatch(addNotification(code, text));
    },
  };
};

export default withRouter(
  injectIntl(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(VklBusinessesView)
  )
);
